/**
 * Gerencia taxas pendentes que precisam ser pagas ao FEE_ADDRESS.
 * Persiste em localStorage para sobreviver a reloads.
 *
 * Fluxo:
 *   1. Cobrança paga chega → IncomingTransferReceivedModal calcula a taxa e adiciona aqui.
 *   2. Usuário pode pagar imediatamente ou depois.
 *   3. Antes de novo recebimento/transferência, TransferForm verifica se o acúmulo atingiu o limite.
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import {
  FEE_PERCENT,
  FEE_BILLING_PERCENT,
  getFeeAddress,
  IS_FEE_ENABLED,
  FEE_ACCUMULATION_LIMIT,
} from '@/config/feeConfig';

const STORAGE_KEY = 'pcp_pending_fees';

const PendingFeeContext = createContext(null);

export function PendingFeeProvider({ children }) {
  const [allFees, setAllFees] = useLocalStorage(STORAGE_KEY, []);

  const pendingFees = useMemo(() => allFees.filter((f) => !f.paid), [allFees]);
  const totalPendingUSD = useMemo(
    () => pendingFees.reduce((sum, f) => sum + (f.feeUSD || 0), 0),
    [pendingFees],
  );
  const hasPendingFees = pendingFees.length > 0;

  /**
   * Verifica se o acúmulo de taxa pendente atingiu o limite configurado.
   * - Se FEE_ACCUMULATION_LIMIT === 0: bloqueia assim que houver qualquer taxa pendente (comportamento restrito).
   * - Se FEE_ACCUMULATION_LIMIT > 0: só bloqueia quando totalPendingUSD >= limite.
   */
  const isOverLimit = useMemo(() => {
    if (!hasPendingFees) return false;
    if (FEE_ACCUMULATION_LIMIT <= 0) return true; // sem tolerância
    return totalPendingUSD >= FEE_ACCUMULATION_LIMIT;
  }, [hasPendingFees, totalPendingUSD]);

  /**
   * Calcula a taxa devida pelo cobrador a partir do valor recebido.
   * Taxa de recebimento (FEE_BILLING_PERCENT) é sempre paga pelo cobrador.
   * Taxa de transferência (FEE_PERCENT): se absorbTransferFee o cobrador pagou (absorveu), senão veio no valor.
   *
   * @param {number} receivedAmount - Valor que o cobrador recebeu na blockchain
   * @param {boolean} absorbTransferFee - true = pagador enviou só o líquido; false = pagador enviou líquido + taxa de transferência
   */
  const calculateFeeFromReceived = useCallback((receivedAmount, absorbTransferFee = false) => {
    if (!IS_FEE_ENABLED || !receivedAmount || receivedAmount <= 0) return 0;
    const totalFeePercent = FEE_PERCENT + FEE_BILLING_PERCENT;
    if (totalFeePercent <= 0) return 0;
    let amountNum;
    if (absorbTransferFee) {
      amountNum = receivedAmount;
    } else {
      amountNum = receivedAmount / (1 + FEE_PERCENT / 100);
    }
    const totalFee = parseFloat((amountNum * totalFeePercent / 100).toFixed(2));
    return totalFee;
  }, []);

  /** Adiciona uma nova taxa pendente. */
  const addPendingFee = useCallback(
    ({ feeUSD, token, chainId, networkName, originTxHash }) => {
      if (!feeUSD || feeUSD <= 0) return null;
      const id = `fee_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const entry = {
        id,
        feeUSD,
        token,
        chainId,
        networkName,
        originTxHash,
        createdAt: Date.now(),
        paid: false,
        feeTxHash: null,
      };
      setAllFees((prev) => [...prev, entry]);
      return id;
    },
    [setAllFees],
  );

  /** Marca uma taxa específica como paga. */
  const markFeePaid = useCallback(
    (id, feeTxHash) => {
      setAllFees((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, paid: true, feeTxHash, paidAt: Date.now() } : f,
        ),
      );
    },
    [setAllFees],
  );

  /** Marca TODAS as taxas pendentes como pagas (pagamento consolidado). */
  const markAllPaid = useCallback(
    (feeTxHash) => {
      setAllFees((prev) =>
        prev.map((f) =>
          !f.paid ? { ...f, paid: true, feeTxHash, paidAt: Date.now() } : f,
        ),
      );
    },
    [setAllFees],
  );

  /** Remove taxas já pagas (limpeza). */
  const clearPaid = useCallback(() => {
    setAllFees((prev) => prev.filter((f) => !f.paid));
  }, [setAllFees]);

  const value = useMemo(
    () => ({
      pendingFees,
      hasPendingFees,
      totalPendingUSD,
      isOverLimit,
      accumulationLimit: FEE_ACCUMULATION_LIMIT,
      calculateFeeFromReceived,
      addPendingFee,
      markFeePaid,
      markAllPaid,
      clearPaid,
      getFeeAddress,
      feeEnabled: IS_FEE_ENABLED,
      feePercent: FEE_PERCENT,
      feeBillingPercent: FEE_BILLING_PERCENT,
    }),
    [pendingFees, hasPendingFees, totalPendingUSD, isOverLimit, calculateFeeFromReceived, addPendingFee, markFeePaid, markAllPaid, clearPaid],
  );

  return (
    <PendingFeeContext.Provider value={value}>{children}</PendingFeeContext.Provider>
  );
}

export function usePendingFees() {
  const ctx = useContext(PendingFeeContext);
  return (
    ctx || {
      pendingFees: [],
      hasPendingFees: false,
      totalPendingUSD: 0,
      isOverLimit: false,
      accumulationLimit: 0,
      calculateFeeFromReceived: () => 0,
      addPendingFee: () => null,
      markFeePaid: () => {},
      markAllPaid: () => {},
      clearPaid: () => {},
      getFeeAddress: () => null,
      feeEnabled: false,
      feePercent: 0,
      feeBillingPercent: 0,
    }
  );
}
